<template>
  <component
    :is="tag"
    ref="root"
    class="sp-button"
    :class="classModifiers"
    part="base"
    :disabled="disabled"
    :aria-label="ariaLabel"
  >
    <div ref="prependSlot" class="sp-button__prepend" :class="prependSlotClassModifiers">
      <slot name="prepend">
        <sp-icon v-if="prependIcon" :name="prependIcon" size="small" />
      </slot>
    </div>
    <div class="sp-button__content" part="content">
      <div v-if="loading" class="sp-button__loading">
        <sp-animated-ellipsis></sp-animated-ellipsis>
      </div>
      <div v-if="icon" class="sp-button__icon" part="icon">
        <sp-icon part="icon" :name="icon" :size="size" />
      </div>
      <div v-else class="sp-button__label" part="label">
        <slot>
          {{ label }}
        </slot>
      </div>
    </div>
    <div ref="appendSlot" class="sp-button__append" :class="appendSlotClassModifiers">
      <slot name="append">
        <sp-icon v-if="appendIcon" :name="appendIcon" size="small" />
      </slot>
    </div>
  </component>
</template>

<script>
import { props as componentProps } from "./props";
</script>

<script setup>
import { watchImmediate } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { useBooleanModifiers } from "../../composables/classes";
import { useColor } from "../../composables/color";
import { useHost } from "../../composables/host";
import { useSize } from "../../composables/size";
import { useVariant } from "../../composables/variant";
import { toBoolean } from "../../utils/props";
import { hasSlotContent } from "../../utils/slots";

const props = defineProps(componentProps);
const root = ref(null);
const { colorModifiers } = useColor(props);
const { variantModifiers } = useVariant(props);
const { sizeModifiers } = useSize(props);
const { booleanModifiers } = useBooleanModifiers(
  props,
  "icon",
  "block",
  "stacked",
  "loading",
  "noWrap",
  "densitySmCompact",
  "disabled",
);

const { host } = useHost(root);
const disabled = ref(toBoolean(props.disabled));
watch(
  () => props.disabled,
  (value) => (disabled.value = toBoolean(value)),
);

const loading = ref(toBoolean(props.loading));
watch(
  () => props.loading,
  (value) => {
    loading.value = toBoolean(value);
  },
);

const ariaLabel = computed(() => {
  const hasActualIcon = props.icon && typeof props.icon === "string" && props.icon.trim().length > 0;
  if (hasActualIcon && props.label) {
    return props.label;
  }
  return undefined;
});

const { isSmScreen } = useBreakpoints(root);
const densitySmCompact = computed(() => toBoolean(props.densitySmCompact) && isSmScreen.value);
const density = computed(() => (densitySmCompact.value ? "compact" : props.density));
const isBlockSmActive = computed(() => toBoolean(props.blockSm) && isSmScreen.value);

function adjustBlockSmActiveCustomProp() {
  const value = isBlockSmActive.value ? 1 : 0;
  host.value?.style.setProperty("--block-sm-active", value.toString());
}

watchImmediate([isBlockSmActive, host], adjustBlockSmActiveCustomProp);

const classModifiers = computed(() => [
  ...colorModifiers.value,
  ...variantModifiers.value,
  ...sizeModifiers.value,
  ...booleanModifiers.value,
  { [`--density-${density.value}`]: density.value !== "default" },
  { "--block-sm": isBlockSmActive.value },
]);

const prependSlot = ref(null);
const hasPrependSlot = computed(() => hasSlotContent(prependSlot.value));
const prependSlotClassModifiers = computed(() => createContentClassModifier(hasPrependSlot.value));

const appendSlot = ref(null);
const hasAppendSlot = computed(() => hasSlotContent(appendSlot.value));
const appendSlotClassModifiers = computed(() => createContentClassModifier(hasAppendSlot.value));

function createContentClassModifier(slot) {
  return { "--has-content": slot };
}
</script>

<style>
:host {
  --bg-color: var(--sp-comp-button-bg-color, #fefefe);
  --text-color: var(--sp-comp-button-text-color, #000);
  --hover-text-color: var(--sp-comp-button-default-hover-text-color, #333);
  --icon-fill-color: var(--sp-comp-button-icon-fill-color, var(--text-color));
  --default-hover-bg-color: var(--sp-comp-button-default-hover-bg-color, var(--bg-color));
  --hover-bg-color: var(--sp-comp-button-hover-bg-color, var(--default-hover-bg-color));
  --hover-bg-opacity: var(--sp-comp-button-hover-bg-opacity, 0);
  --border-color: var(--sp-comp-button-border-color, transparent);
  --hover-border-color: var(--sp-comp-button-hover-border-color, var(--border-color, transparent));
  --border-width: var(--sp-comp-button-border-width, 0);
  --border-style: var(--sp-comp-button-border-style, solid);
  --button-border-radius: var(--sp-comp-button-border-radius, 0);
  --transition-time: 0.3s;
  --font-family: var(--sp-comp-button-font-family, var(--sp-sys-typescale-body-font-family));
  --font-weight: var(--sp-comp-button-font-weight, var(--sp-sys-typescale-body-font-weight));
  --font-size: var(--sp-comp-button-font-size, var(--sp-sys-typescale-body-font-size));
  --line-height: var(--sp-comp-button-line-height, var(--sp-sys-typescale-body-line-height));
  --font-short-default: var(--font-weight) var(--font-size) / var(--line-height) var(--font-family);
  --font-short: var(--sp-comp-button-font-short, var(--font-short-default));
  --letter-spacing: var(--sp-comp-button-letter-spacing, normal);
  --button-display: var(--sp-comp-button-display, inline-grid);
  --button-min-width: var(--sp-comp-button-min-width, minmax(fit-content, 9rem));
  --button-width: var(--sp-comp-button-width, auto);
  --button-default-height: var(--sp-comp-button-size, var(--sp-sys-form-field-min-height, 3rem));
  --size: var(--button-default-height);
  --button-min-height: var(--sp-comp-button-min-height, auto);
  --button-justify-content: var(--sp-comp-button-justify-content, center);
  --button-content-text-align: var(--sp-comp-button-content-text-align, center);
  --button-content-wrap: var(--sp-comp-button-content-wrap, normal);
  --button-content-text-overflow: var(--sp-comp-button-content-text-overflow, ellipsis);
  --button-content-overflow: var(--sp-comp-button-content-overflow, visible);
  --button-gap: var(--sp-comp-button-gap, 0.5ch);
  --button-padding-inline: var(--sp-comp-button-padding-inline, 0);
  --button-padding-block: var(--sp-comp-button-padding-block, 1rem);
  --button-padding: var(--sp-comp-button-padding, var(--button-padding-inline) var(--button-padding-block));
  --button-focus-outline-offset: var(--sp-comp-button-focus-outline-offset, 0);
  --button-focus-text-decoration: var(--sp-comp-button-focus-text-decoration, none);
  display: inline-grid;
  width: var(--button-width);
  box-sizing: border-box;
  border-radius: var(--button-border-radius);
  border-width: 0;
}

:host([variant="text-inline"]),
:host([variant="appearance-link"]) {
  display: inline;
  --button-display: inline-flex;
}

:host([block]) {
  display: grid;
  min-width: 100%;
}

:host([block-sm]) {
  min-width: calc(var(--block-sm-active, 0) * 100%);
}
</style>

<style lang="scss" scoped>
@import "../../shared/colors.scss";

.sp-button {
  align-items: center;
  background: var(--bg-color);
  border-color: var(--border-color);
  border-radius: var(--button-border-radius);
  border-style: var(--border-style);
  border-width: var(--border-width);
  box-sizing: border-box;
  color: var(--text-color);
  cursor: pointer;
  display: var(--button-display);
  font: var(--font-short);
  grid-template-areas: "prepend content append";
  grid-template-columns: max-content auto max-content;
  height: var(--size);
  min-height: var(--button-min-height);
  justify-content: var(--button-justify-content);
  min-width: var(--button-min-width);
  width: var(--button-width);
  padding: var(--button-padding);
  position: relative;
  text-decoration: none;
  text-transform: var(--sp-comp-button-text-transform, none);
  letter-spacing: var(--letter-spacing);

  &__label {
    display: flex;
    align-items: center;
  }

  // ::before controls the background hover
  // ::after controls the focus
  &::before,
  &::after {
    position: absolute;
    content: "";
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    border-radius: inherit;
    box-sizing: border-box;
    transition: opacity var(--transition-time) linear;
  }

  // Element for Background hover
  &::before {
    background: var(--hover-bg-color) !important;
  }

  &:hover:not(.--disabled) {
    color: var(--hover-text-color);
    border-color: var(--hover-border-color);

    &::before {
      opacity: var(--hover-bg-opacity);
    }
  }

  &.--variant-outlined {
    --border-width: var(--sp-comp-button-outlined-border-width, 1px);
    --border-style: var(--sp-comp-button-outlined-border-style, solid);
    --inner-border-radius: calc(var(--button-border-radius) - var(--border-width));
    background: transparent;

    &::before {
      border-radius: var(--inner-border-radius);
    }

    &:focus,
    &:focus-visible,
    &:focus-within {
      &::after {
        outline-offset: calc(var(--button-focus-outline-offset) + var(--border-width));
      }
    }
  }

  &.--variant-text {
    --border-width: 0;
  }

  &.--variant-text-inline {
    &::after,
    &::before {
      top: -0.75ex;
      right: -0.5ch;
      bottom: -0.75ex;
      left: -0.5ch;
    }
  }

  &.--variant-text-inline,
  &.--variant-appearance-link {
    --button-min-height: auto;
    padding: 0 !important;
    height: auto !important;
    min-width: 0 !important;
    gap: 0 !important;
  }

  &.--variant-appearance-link {
    --bg-color: transparent !important;
    --hover-bg-opacity: var(--sp-comp-button-appearance-link-hover-bg-opacity, 0);
    --text-color: var(--sp-comp-button-appearance-link-text-color, var(--sp-sys-color-link, #007078)) !important;
    --hover-text-color: var(--sp-comp-button-appearance-link-hover-text-color, var(--text-color)) !important;
    --border-color: transparent;
    --border-width: 0;
    --border-style: none;
    --button-display: inline;
    text-decoration: var(--sp-comp-button-appearance-link-text-decoration, underline);

    &::before,
    &::after {
      display: none;
    }
  }

  &.--block-sm,
  &.--block {
    width: 100%;
  }

  &.--stacked {
    grid-template-areas: "prepend" "content" "append";
    justify-items: center;
    min-height: fit-content;
    height: auto;

    .sp-button__content,
    .sp-button__label,
    .sp-button__icon {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  &.--loading {
    cursor: wait;
    .sp-button__label,
    .sp-button__icon {
      opacity: 0;
    }
  }

  &.--no-wrap {
    white-space: nowrap;
  }

  // Sizes
  &.--size-xx-small {
    --size: var(--sp-comp-button-xx-small-height, 1.25rem);
    font-size: var(--sp-comp-button-xx-small-font-size, 0.5rem);
    padding: var(--sp-comp-button-xx-small-padding, 0 0.25rem);
  }

  &.--size-x-small {
    --size: var(--sp-comp-button-x-small-height, 1.5rem);
    font-size: var(--sp-comp-button-x-small-font-size, 0.75rem);
    padding: var(--sp-comp-button-x-small-padding, 0 0.5rem);
  }

  &.--size-small {
    --size: var(--sp-comp-button-small-height, 2rem);
    font: var(--sp-comp-button-small-font-short, var(--sp-sys-typescale-small-bold-short));
    padding: var(--sp-comp-button-small-padding, 0 0.75rem);
  }

  &.--size-large {
    --size: var(--sp-comp-button-large-height, 3.75rem);
    font-size: var(--sp-comp-button-large-font-size, 1.375rem);
    padding: var(--sp-comp-button-large-padding, 0 1.875rem);
  }

  &.--icon {
    --button-content-overflow: hidden;
    --button-border-radius: 50%;
    --icon-size: var(--size);
    display: grid;
    padding: 0;
    min-width: var(--size);
    width: var(--size);
    min-height: var(--size);
    max-height: var(--size);
    justify-content: center;
    align-items: center;
    grid-template-areas: "content";
    grid-template-columns: auto;
    grid-template-rows: auto;

    sp-icon {
      --size: var(--icon-size);
      --fill: var(--sp-comp-button-icon-fill-color, var(--text-color));
    }

    .sp-button__content,
    .sp-button__icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .sp-button__prepend,
    .sp-button__append {
      display: none;
    }
  }

  &.--density-compact {
    --size: var(
      --sp-comp-button-compact-height,
      var(--sp-sys-form-field-compact-min-height, var(--button-default-height))
    );
  }

  // Colors
  @each $color in primary, secondary, danger, text, error, warning, success, info, "white", custom {
    &.--color-#{$color} {
      --text-color: var(--sp-comp-button-#{$color}-text-color, var(--sp-sys-color-on-#{$color}, #fff));
      --bg-color: var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #{get-color($color)}));
      --default-hover-text-color: var(--sp-sys-color-on-#{$color}, #fff);
      --hover-text-color: var(--sp-comp-button-#{$color}-hover-text-color, var(--default-hover-text-color));
      --hover-bg-color: var(--sp-comp-button-#{$color}-hover-bg-color, var(--default-hover-bg-color));
      --icon-fill-color: var(--text-color);

      &.--variant-text-inline,
      &.--variant-text {
        --bg-color: transparent;
        --text-color: var(
          --sp-comp-button-#{$color}-variant-text-text-color,
          var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #666))
        );
        --hover-text-color: var(--sp-comp-button-#{$color}-hover-variant-text-text-color, var(--text-color));
        --hover-bg-color: var(
          --sp-comp-button-#{$color}-hover-outlined-bg-color,
          var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #666))
        );
        --hover-bg-opacity: var(--sp-comp-button-#{$color}-hover-variant-text-bg-opacity, 0.1);
      }

      &.--variant-outlined {
        --text-color: var(
          --sp-comp-button-#{$color}-outlined-text-color,
          var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #666))
        );
        --icon-fill-color: var(--text-color);
        --border-color: var(--sp-comp-button-outlined-border-color, var(--text-color));
        --hover-text-color: var(--sp-comp-button-#{$color}-hover-outlined-text-color, var(--default-hover-text-color));
        --hover-bg-color: var(--sp-comp-button-#{$color}-hover-outlined-bg-color, var(--bg-color));
        --hover-bg-opacity: var(--sp-comp-button-#{$color}-hover-outlined-bg-opacity, 1);
        --hover-border-color: var(
          --sp-comp-button-hover-outlined-border-color,
          var(--sp-comp-button-#{$color}-outlined-hover-border-color, var(--border-color))
        );

        &:focus,
        &:focus-visible,
        &:focus-within {
          --border-color: var(--sp-comp-button-focus-outlined-border-color, var(--text-color));
        }

        &:hover {
          --icon-fill-color: var(--hover-text-color);

          sp-icon {
            --fill: var(--hover-text-color);
          }

          & ::slotted(sp-icon) {
            --fill: var(--hover-text-color);
          }
        }
      }
    }
  }

  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
    box-shadow: none;
    text-decoration: var(--button-focus-text-decoration);
  }

  &:focus,
  &:focus-within {
    &::after {
      opacity: var(--sp-comp-button-focus-opacity, 1);
      outline: var(--sp-comp-button-focus-outline, 1px solid #ccc);
      outline-offset: var(--sp-comp-button-focus-outline-offset, 0);
      box-shadow: var(--sp-comp-button-focus-box-shadow, none);
      border-radius: var(--sp-comp-button-focus-border-radius, none);
    }
  }

  // enable special syling for key-focus if needed
  &:focus-visible::after {
    box-shadow: var(--sp-comp-button-focus-visible-box-shadow, var(--sp-comp-button-focus-box-shadow, none));
    outline: var(--sp-comp-button-focus-visible-outline, var(--sp-comp-button-focus-outline, 1px solid #ccc));
    outline-offset: var(--sp-comp-button-focus-visible-outline-offset, var(--sp-comp-button-focus-outline-offset, 0));
    opacity: var(--sp-comp-button-focus-visible-opacity, var(--sp-comp-button-focus-opacity, 1));
  }

  &.--disabled {
    --disabled-text-color: var(--sp-comp-button-disabled-text-color, var(--text-color));
    --disabled-background-color: var(--sp-comp-button-disabled-background, inherit);

    background: var(--disabled-background-color);
    color: var(--disabled-text-color);
    cursor: var(--sp-comp-button-disabled-cursor, default);
    opacity: var(--sp-comp-button-disabled-opacity, 0.3);

    &.--variant-outlined,
    &.--variant-text-inline,
    &.--variant-text {
      background: transparent;
      color: var(--disabled-background-color);
    }

    &.--variant-outlined {
      --border-color: var(--disabled-background-color);
    }
  }
}

.sp-button__content {
  grid-area: content;
  z-index: 1;
  text-align: var(--button-content-text-align);
  overflow: var(--button-content-overflow);
}

.sp-button__prepend,
.sp-button__append {
  display: flex;
  position: relative;
  z-index: 1;
}

.sp-button__prepend {
  grid-area: prepend;
  left: calc(var(--button-gap) * -1);

  & ::slotted(sp-icon) {
    --fill: var(--sp-comp-button-prepend-icon-fill, var(--icon-fill-color));
    --size: var(--sp-comp-button-prepend-icon-size, 2rem);
  }
}

.sp-button__append {
  grid-area: append;
  right: calc(var(--button-gap) * -1);

  & ::slotted(sp-icon) {
    --fill: var(--sp-comp-button-append-icon-fill, var(--icon-fill-color));
    --size: var(--sp-comp-button-append-icon-size, 2rem);
  }
}

.sp-button__loading {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: hidden;
}
</style>
